import React, { useState, useEffect } from "react";
import { Alert, Modal, Button, Form } from "react-bootstrap";
import axios from '../../../api/axios';
import { isDeliveredOrder, ORDER_STATUS_OPTIONS } from '../../../utils/orderStatus';

type EditOrderModalProps = {
    show: boolean;
    setShow: (value: boolean) => void;
    token: string | null;
    order: any;
    deliveryBoys: Array<{
        _id: string;
        name: string;
        phoneNumber?: string | null;
    }>;
    deliveryUsers: Array<{
        _id: string;
        username: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    }>;
    onSaved: () => Promise<void> | void;
};

const EditOrderModal = ({ show, setShow, order, token, deliveryBoys, deliveryUsers, onSaved }: EditOrderModalProps) => {
    const [error, setError] = useState('');

    const [updatedOrder, setUpdatedOrder] = useState({
        trackingNumber: '',
        status: 'PLACED',
        paymentStatus: 'unpaid',
        deliveryBoyId: '',
        deliveryBoyUserId: '',
        deliveryNote: '',
        failureReason: '',
        allowReopenDelivered: false,
        reopenReason: '',
    });

    useEffect(() => {
        if (order) {
            setUpdatedOrder({
                trackingNumber: order.trackingNumber || '',
                status: order.status || 'PLACED',
                paymentStatus: order.paymentStatus || 'unpaid',
                deliveryBoyId: order?.deliveryBoy?.id || '',
                deliveryBoyUserId: order?.deliveryBoyUserId || '',
                deliveryNote: order.deliveryNote || '',
                failureReason: order.failureReason || '',
                allowReopenDelivered: false,
                reopenReason: '',
            });
            setError('');
        }
    }, [order]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
        setUpdatedOrder((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleUpdate = async () => {
        if (!order?._id) {
            return;
        }
        if (!token) {
            setError('Authentication is required. Please login again.');
            return;
        }

        const isDeliveredState = isDeliveredOrder(order?.status);
        const isReopenAttempt = isDeliveredState && updatedOrder.status !== order?.status;

        if (updatedOrder.status === 'CANCELLED' && !updatedOrder.failureReason.trim()) {
            setError('Failure reason is required when cancelling an order.');
            return;
        }

        if (isReopenAttempt && !updatedOrder.allowReopenDelivered) {
            setError('Please confirm reopen before changing a delivered order status.');
            return;
        }

        if (isReopenAttempt && !updatedOrder.reopenReason.trim()) {
            setError('Reopen reason is required for delivered order correction.');
            return;
        }

        try {
            await axios.put(
                `/orders/${order._id}/status`,
                {
                    status: updatedOrder.status,
                    paymentStatus: updatedOrder.paymentStatus,
                    trackingNumber: updatedOrder.trackingNumber.trim() || undefined,
                    deliveryBoyId: updatedOrder.deliveryBoyId || undefined,
                    deliveryBoyUserId: updatedOrder.deliveryBoyUserId || undefined,
                    deliveryNote: updatedOrder.deliveryNote.trim() || undefined,
                    failureReason: updatedOrder.status === 'failed' ? updatedOrder.failureReason.trim() || undefined : undefined,
                    allowReopenDelivered: isReopenAttempt ? updatedOrder.allowReopenDelivered : undefined,
                    reopenReason: isReopenAttempt ? updatedOrder.reopenReason.trim() || undefined : undefined,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            await onSaved();
            setShow(false);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to update order.');
        }
    };

    return (
        <Modal show={show} onHide={() => setShow(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select name="status" value={updatedOrder.status} onChange={handleChange}>
                            {ORDER_STATUS_OPTIONS.map((status) => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Payment Status</Form.Label>
                        <Form.Select name="paymentStatus" value={updatedOrder.paymentStatus} onChange={handleChange}>
                            <option value="unpaid">Unpaid</option>
                            <option value="paid">Paid</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Tracking Number (optional)</Form.Label>
                        <Form.Control
                            name="trackingNumber"
                            value={updatedOrder.trackingNumber}
                            onChange={handleChange}
                            placeholder="TRK-123"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Assign Delivery Boy (optional)</Form.Label>
                        <Form.Select name="deliveryBoyId" value={updatedOrder.deliveryBoyId} onChange={handleChange}>
                            <option value="">Unassigned</option>
                            {deliveryBoys.map((deliveryBoy) => {
                                return (
                                    <option key={deliveryBoy._id} value={deliveryBoy._id}>
                                        {deliveryBoy.name}{deliveryBoy.phoneNumber ? ` (${deliveryBoy.phoneNumber})` : ''}
                                    </option>
                                );
                            })}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Assign Delivery Login User (optional)</Form.Label>
                        <Form.Select name="deliveryBoyUserId" value={updatedOrder.deliveryBoyUserId} onChange={handleChange}>
                            <option value="">Unassigned</option>
                            {deliveryUsers.map((deliveryUser) => {
                                const displayName = `${deliveryUser.firstName || ''} ${deliveryUser.lastName || ''}`.trim() || deliveryUser.username;
                                return (
                                    <option key={deliveryUser._id} value={deliveryUser._id}>
                                        {displayName} ({deliveryUser.username})
                                    </option>
                                );
                            })}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Note (optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            name="deliveryNote"
                            value={updatedOrder.deliveryNote}
                            onChange={handleChange}
                            placeholder="Add rider or handoff notes"
                        />
                    </Form.Group>

                    {updatedOrder.status === 'CANCELLED' ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Failure Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="failureReason"
                                value={updatedOrder.failureReason}
                                onChange={handleChange}
                                placeholder="Reason for failed delivery attempt"
                            />
                        </Form.Group>
                    ) : null}

                    {/* Reopen confirmation is intentionally hidden in phase-1 simplified flow. */}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleUpdate}>Update Order</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditOrderModal;

